import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api.ts';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext.tsx';
import { maskCurrency } from '../utils/balanceMask.ts';
import { formatCurrency, type CurrencyCode } from '../utils/dateUtils.ts';
import { fetchUsdToBrlRate, convertUsdToBrl } from '../services/exchangeRateApi.ts';
import './Accounts.css';

interface Account {
  id: number;
  name: string;
  description: string;
  balance: number;
  currency: string;
  is_default: boolean;
  is_investment: boolean;
  created_at: string;
  updated_at: string;
}

const Accounts: React.FC = () => {
  const { isVisible } = useBalanceVisibility();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    balance: 0,
    currency: 'BRL' as CurrencyCode,
    is_default: false,
    is_investment: false
  });
  const [balanceInput, setBalanceInput] = useState('');
  const [usdBrlRate, setUsdBrlRate] = useState<{ rate: number; date: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string, includeTime: boolean = false) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      
      if (includeTime) {
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };

  const getAccountCurrency = (a: Account): CurrencyCode =>
    (a.currency === 'USD' ? 'USD' : 'BRL') as CurrencyCode;

  const cashBalanceBRL = accounts
    .filter((a) => !a.is_investment && getAccountCurrency(a) === 'BRL')
    .reduce((sum, a) => sum + (Number.isFinite(a.balance) ? a.balance : 0), 0);
  const cashBalanceUSD = accounts
    .filter((a) => !a.is_investment && getAccountCurrency(a) === 'USD')
    .reduce((sum, a) => sum + (Number.isFinite(a.balance) ? a.balance : 0), 0);

  const investmentBalanceBRL = accounts
    .filter((a) => a.is_investment && getAccountCurrency(a) === 'BRL')
    .reduce((sum, a) => sum + (Number.isFinite(a.balance) ? a.balance : 0), 0);
  const investmentBalanceUSD = accounts
    .filter((a) => a.is_investment && getAccountCurrency(a) === 'USD')
    .reduce((sum, a) => sum + (Number.isFinite(a.balance) ? a.balance : 0), 0);

  // Funções para formatação de valor monetário conforme moeda
  const formatBalanceForDisplay = (value: number, currency: CurrencyCode): string => {
    if (value === 0) return '';
    const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
    return value.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseBalanceFromInput = (input: string, currency: CurrencyCode): number => {
    if (!input) return 0;
    const cleanInput = input.replace(/[^\d,.]/g, '');
    let numericString: string;
    if (currency === 'BRL') {
      // BRL: 1.234,56 (ponto = milhares, vírgula = decimal)
      numericString = cleanInput.replace(/\./g, '').replace(',', '.');
    } else {
      // USD: 1,234.56 (vírgula = milhares, ponto = decimal)
      numericString = cleanInput.replace(/,/g, '');
    }
    return parseFloat(numericString) || 0;
  };

  const handleBalanceChange = (input: string) => {
    setBalanceInput(input);
    const numericValue = parseBalanceFromInput(input, formData.currency);
    setFormData({ ...formData, balance: numericValue });
  };

  const handleBalanceBlur = () => {
    if (formData.balance > 0) {
      setBalanceInput(formatBalanceForDisplay(formData.balance, formData.currency));
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (investmentBalanceUSD !== 0) {
      fetchUsdToBrlRate().then(setUsdBrlRate);
    }
  }, [investmentBalanceUSD]);

  // Scroll automático quando o formulário for aberto
  useEffect(() => {
    if (showForm && formRef.current) {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        formRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        // Fallback: scroll para o topo da página se o ref não funcionar
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [showForm]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/v1/accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Erro ao buscar contas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAccount) {
        await api.put(`/api/v1/accounts/${editingAccount.id}`, formData);
      } else {
        await api.post('/api/v1/accounts', formData);
      }
      setFormData({ name: '', description: '', balance: 0, currency: 'BRL', is_default: false, is_investment: false });
      setBalanceInput('');
      setShowForm(false);
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    const currency = getAccountCurrency(account);
    setFormData({
      name: account.name,
      description: account.description,
      balance: account.balance,
      currency,
      is_default: account.is_default || false,
      is_investment: account.is_investment || false
    });
    setBalanceInput(account.balance > 0 ? formatBalanceForDisplay(account.balance, currency) : '');
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAccount(null);
    setFormData({ name: '', description: '', balance: 0, currency: 'BRL', is_default: false, is_investment: false });
    setBalanceInput('');
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta conta?')) {
      setDeletingId(id);
      try {
        await api.delete(`/api/v1/accounts/${id}`);
        fetchAccounts();
      } catch (error) {
        console.error('Erro ao excluir conta:', error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="accounts">
        <div className="page-header">
          <h1>🏦 Contas</h1>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="accounts">
      <div className="page-header">
        <h1>🏦 Contas</h1>
        <p>Gerencie suas contas bancárias e carteiras</p>
        <button 
          className="primary-button"
          onClick={() => {
            setEditingAccount(null);
            setFormData({ name: '', description: '', balance: 0, currency: 'BRL', is_default: false, is_investment: false });
            setBalanceInput('');
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancelar' : 'Nova Conta'}
        </button>
      </div>

      {showForm && (
        <div className="form-container" ref={formRef}>
          <form onSubmit={handleSubmit} className="account-form">
            <h3>{editingAccount ? 'Editar Conta' : 'Nova Conta'}</h3>
            <div className="form-group">
              <label htmlFor="name">Nome da Conta</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ex: Conta Corrente, Poupança..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <input
                type="text"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
            <div className="form-group">
              <label htmlFor="currency">Moeda</label>
              <select
                id="currency"
                value={formData.currency}
                onChange={(e) => {
                  const newCurrency = e.target.value as CurrencyCode;
                  setFormData({ ...formData, currency: newCurrency });
                  if (formData.balance > 0) {
                    setBalanceInput(formatBalanceForDisplay(formData.balance, newCurrency));
                  }
                }}
              >
                <option value="BRL">Real Brasileiro (R$)</option>
                <option value="USD">Dólar Americano (US$)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="balance">Saldo Inicial</label>
              <input
                type="text"
                id="balance"
                value={balanceInput}
                onChange={(e) => handleBalanceChange(e.target.value)}
                onBlur={handleBalanceBlur}
                onFocus={() => {
                  // Ao focar, mostra o valor formatado para edição
                  if (formData.balance > 0) {
                    setBalanceInput(formatBalanceForDisplay(formData.balance, formData.currency));
                  }
                }}
                placeholder={formData.currency === 'BRL' ? '0,00' : '0.00'}
              />
            </div>

            {/* Toggle para definir como padrão */}
            <div className="form-group form-group-toggle">
              <div className="toggle-container">
                <div className="toggle-label-with-tooltip">
                  <label htmlFor="is_default" className="toggle-label">
                    Definir como padrão
                  </label>
                  <span className="tooltip">
                    <button
                      type="button"
                      className="tooltip-trigger"
                      aria-label="Ajuda: Definir como padrão"
                      aria-describedby="is_default_help"
                    >
                      ?
                    </button>
                    <span id="is_default_help" role="tooltip" className="tooltip-content">
                      Esta conta será pré-selecionada ao criar novas transações
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* Toggle para marcar conta como investimento */}
            <div className="form-group form-group-toggle">
              <div className="toggle-container">
                <div className="toggle-label-with-tooltip">
                  <label htmlFor="is_investment" className="toggle-label">
                    É uma conta de investimento
                  </label>
                  <span className="tooltip">
                    <button
                      type="button"
                      className="tooltip-trigger"
                      aria-label="Ajuda: Conta de investimento"
                      aria-describedby="is_investment_help"
                    >
                      ?
                    </button>
                    <span id="is_investment_help" role="tooltip" className="tooltip-content">
                      Útil para filtros e relatórios futuros (não altera regras de saldo)
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="is_investment"
                    checked={formData.is_investment}
                    onChange={(e) => setFormData({ ...formData, is_investment: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            {/* Informações de criação e atualização - apenas no modo de edição */}
            {editingAccount && (
              <div className="account-metadata">
                <div className="metadata-item">
                  <span className="metadata-label">Criado em:</span>
                  <span className="metadata-value">{formatDate(editingAccount.created_at, true)}</span>
                </div>
                {editingAccount.updated_at && editingAccount.updated_at !== editingAccount.created_at && (
                  <div className="metadata-item">
                    <span className="metadata-label">Última atualização:</span>
                    <span className="metadata-value">{formatDate(editingAccount.updated_at, true)}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingAccount ? 'Salvar Alterações' : 'Criar Conta')}
              </button>
              <button 
                type="button" 
                className="secondary-button"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="accounts-content">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏦</div>
            <h3>Nenhuma conta cadastrada</h3>
            <p>Comece adicionando suas contas bancárias para organizar suas finanças</p>
            <button 
              className="primary-button"
              onClick={() => {
                setEditingAccount(null);
                setFormData({ name: '', description: '', balance: 0, currency: 'BRL', is_default: false, is_investment: false });
                setBalanceInput('');
                setShowForm(true);
              }}
            >
              Adicionar Conta
            </button>
          </div>
        ) : (
          <>
            <div className="accounts-summary-cards">
              <div className="accounts-summary-card">
                <div className="accounts-summary-icon">💵</div>
                <div className="accounts-summary-content">
                  <h3>Saldo (Caixa)</h3>
                  <div className="accounts-summary-values">
                    {(cashBalanceBRL !== 0 || cashBalanceUSD !== 0) ? (
                      <>
                        {cashBalanceBRL !== 0 && (
                          <p className={`accounts-summary-value ${cashBalanceBRL >= 0 ? 'positive' : 'negative'}`}>
                            {isVisible ? formatCurrency(cashBalanceBRL, 'BRL') : maskCurrency(formatCurrency(cashBalanceBRL, 'BRL'), 'BRL')}
                          </p>
                        )}
                        {cashBalanceUSD !== 0 && (
                          <p className={`accounts-summary-value ${cashBalanceUSD >= 0 ? 'positive' : 'negative'}`}>
                            {isVisible ? formatCurrency(cashBalanceUSD, 'USD') : maskCurrency(formatCurrency(cashBalanceUSD, 'USD'), 'USD')}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="accounts-summary-value positive">
                        {isVisible ? formatCurrency(0, 'BRL') : maskCurrency(formatCurrency(0, 'BRL'), 'BRL')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="accounts-summary-card">
                <div className="accounts-summary-icon">📈</div>
                <div className="accounts-summary-content">
                  <h3>Saldo (Investimentos)</h3>
                  <div className="accounts-summary-values">
                    {(investmentBalanceBRL !== 0 || investmentBalanceUSD !== 0) ? (
                      <>
                        {investmentBalanceBRL !== 0 && (
                          <p className={`accounts-summary-value ${investmentBalanceBRL >= 0 ? 'positive' : 'negative'}`}>
                            {isVisible ? formatCurrency(investmentBalanceBRL, 'BRL') : maskCurrency(formatCurrency(investmentBalanceBRL, 'BRL'), 'BRL')}
                          </p>
                        )}
                        {investmentBalanceUSD !== 0 && (
                          <p className={`accounts-summary-value ${investmentBalanceUSD >= 0 ? 'positive' : 'negative'}`}>
                            {isVisible
                              ? formatCurrency(investmentBalanceUSD, 'USD')
                              : maskCurrency(formatCurrency(investmentBalanceUSD, 'USD'), 'USD')}
                            {usdBrlRate && (
                              <span
                                className="accounts-summary-usd-conversion"
                                title={`Cotação utilizada: 1 US$ = R$ ${usdBrlRate.rate.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} (${usdBrlRate.date})`}
                              >
                                {' '}
                                ({isVisible
                                  ? formatCurrency(convertUsdToBrl(investmentBalanceUSD, usdBrlRate.rate), 'BRL')
                                  : maskCurrency(formatCurrency(convertUsdToBrl(investmentBalanceUSD, usdBrlRate.rate), 'BRL'), 'BRL')})
                              </span>
                            )}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="accounts-summary-value positive">
                        {isVisible ? formatCurrency(0, 'BRL') : maskCurrency(formatCurrency(0, 'BRL'), 'BRL')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="accounts-grid">
              {accounts.map((account) => (
                <div key={account.id} className="account-card">
                  <div className="account-header">
                    <h3>
                      {account.name}
                      {account.is_investment && (
                        <span title="Conta de investimento"> • Investimento</span>
                      )}
                    </h3>
                    <div className="account-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleEdit(account)}
                        title="Editar conta"
                      >
                        <img src="/edit.svg" alt="Editar" className="icon" />
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDelete(account.id)}
                        title="Excluir conta"
                        disabled={deletingId === account.id}
                      >
                        {deletingId === account.id ? '...' : <img src="/delete.svg" alt="Excluir" className="icon" />}
                      </button>
                    </div>
                  </div>
                  {account.description && (
                    <p className="account-description">{account.description}</p>
                  )}
                  <div className="account-balance">
                    <span className="balance-label">Saldo:</span>
                    <span className={`balance-value ${account.balance >= 0 ? 'positive' : 'negative'}`}>
                      {isVisible
                        ? formatCurrency(account.balance, getAccountCurrency(account))
                        : maskCurrency(formatCurrency(account.balance, getAccountCurrency(account)), getAccountCurrency(account))}
                    </span>
                  </div>
                  <div className="account-meta">
                    <small>Criada em: {formatDate(account.created_at)}</small>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Accounts;